import { useEffect, useState } from 'react';
import type { Meta } from '../api';
import { dayName } from '../calendar';
import { BadgeRow } from '../badges';

/**
 * Info in de speler (zoals DexHub): het logo of de titel groot, de aflevering, labels voor beeld en geluid,
 * IMDb/TMDB, wanneer de serie terugkomt; rechtsboven de klok en hoe laat het afgelopen is.
 * Zelfde opbouw in de Plex-speler en de addon-speler (en in de apps).
 */

export interface OsdInfo {
  title: string;
  /** "S2 · A3 · titel" of het jaar */
  line?: string | null;
  flags: string[];
}

function hhmm(d: Date): string {
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

/** Klok rechtsboven, met "Eindigt om" als we weten hoe lang het nog duurt. */
export function PlayerClock({ remaining }: { remaining: number | null }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="player-clock" aria-hidden="true">
      <strong>{hhmm(now)}</strong>
      {remaining !== null && remaining > 0 && <span>Eindigt om {hhmm(new Date(now.getTime() + remaining * 1000))}</span>}
    </div>
  );
}

/** Groot blok linksonder, boven de tijdlijn: alleen als de bediening open is. */
export function PlayerInfoPanel({ info, meta }: { info: OsdInfo; meta: Meta | null }) {
  const imdb = meta?.ratings?.imdb;
  const tmdb = meta?.ratings?.tmdb;
  const next = meta?.nextEpisode;
  return (
    <div className="player-osd" aria-label="Over deze titel">
      {meta?.logo ? <img className="player-osd-logo" src={meta.logo} alt={info.title} /> : <h2 className="player-osd-title">{info.title}</h2>}
      {info.line && <div className="player-osd-line">{info.line}</div>}
      <div className="player-osd-meta">
        {imdb ? <span className="osd-rating"><span className="osd-imdb">IMDb</span>{imdb.toFixed(1)}</span> : null}
        {tmdb ? <span className="osd-rating"><span className="osd-tmdb">TMDB</span>{tmdb > 10 ? `${Math.round(tmdb)}%` : tmdb.toFixed(1)}</span> : null}
        {meta?.certification && <span className="osd-cert">{meta.certification}</span>}
        {next && <span className="osd-next">Nieuwe aflevering {dayName(next.date, true)}</span>}
      </div>
      {info.flags.length > 0 && <BadgeRow labels={info.flags} place="player" size="lg" />}
    </div>
  );
}

/** Taalcode onder een knop (ENG, NLD), zoals DexHub. */
export function langCode(l: string | null | undefined): string | null {
  if (!l) return null;
  return l.slice(0, 3).toUpperCase();
}

/** Labels uit de naam van een addon-bron (4K, DV, HEVC, Atmos…), in dezelfde vorm als bij Plex. */
export function streamFlags(s: { quality: string | null; traits?: { hdr: string | null; codec: string | null; audio: string | null } } | null | undefined): string[] {
  if (!s) return [];
  const out: string[] = [];
  if (s.quality) out.push(s.quality === '4K' ? '4K' : s.quality);
  if (s.traits?.hdr === 'dv') out.push('Dolby Vision');
  else if (s.traits?.hdr === 'hdr') out.push('HDR');
  if (s.traits?.codec) out.push(s.traits.codec.toUpperCase());
  const a = s.traits?.audio;
  if (a) out.push(a === 'atmos' ? 'Atmos' : a === 'truehd' ? 'TrueHD' : a === 'dts' ? 'DTS' : a === 'ddp' ? 'DD+' : 'AAC');
  return out;
}
